import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../../../../shared/components/loaders/page-loader";
import { formatTcV4Decimal } from "../../../../tc-v4-decimals";

export const initIncomingTcStandardMap: Record<string, string[]> = {};

const unwrapIncomingProducts = (payload: any): any[] => {
  if (!payload) return [];
  let data = payload;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (data?.response !== undefined) return unwrapIncomingProducts(data.response);
  const rows = Array.isArray(data) ? data : [data];
  return rows
    .flatMap((row: any) => {
      const value = row?.trackitPortalData ?? row;
      return Array.isArray(value) ? value : [value];
    })
    .filter(Boolean);
};

type SourceRawMaterialRow = {
  tcProductCategoryCode?: string;
  tcProductDetailCode?: string;
  rawMaterialCode?: string;
  rawMaterialPercentage?: number;
  standard?: string;
  isCertified?: string;
  is_certified?: string;
  tcProductNetShippingWeight?: number;
  tcProductSupplementaryWeight?: number;
  tcProductAdditivesWeight?: number;
};

type IncomingTcStandardProps = {
  formRef?: any;
  componentData: Record<string, string[]>;
  setComponentData: (data: Record<string, string[]>) => void;
  selectedArticles: string[];
  onSelectionChange: (articles: string[]) => void;
  allArticles: string[];
  standardsByArticle?: Record<string, string[]>;
  rawMaterialsByArticle?: Record<string, SourceRawMaterialRow[]>;
  sourceByArticle?: Record<string, SourceRawMaterialRow>;
  incomingTcType?: string;
  // TC-level standards of the incoming certificate; used when the source
  // data has no per-article standards.
  fallbackStandards?: string[];
  // Incoming source document id, used to enrich raw-material details from
  // the incoming source entity (get-incoming-tc-info) when LIST data is thin.
  effectiveTcId?: string;
  // True while the source article maps are still being resolved; shows a
  // loader instead of the hard "no products" message.
  sourceLoading?: boolean;
  onSourceRowsChange?: (rowsByArticle: Record<string, SourceRawMaterialRow[]>) => void;
};

const formatWeight = (value: any): string => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? formatTcV4Decimal(num) : "-";
};

const formatPercentage = (value: any): string => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? `${num}%` : "";
};

// One merged row per incoming product article, per the design walkthrough:
// checkbox | product no | standards | PC | PD | RMs (stacked) [| weights for V4/ATU]
const IncomingTcStandard: React.FC<IncomingTcStandardProps> = ({
  componentData,
  setComponentData,
  selectedArticles = [],
  onSelectionChange,
  allArticles = [],
  standardsByArticle = {},
  rawMaterialsByArticle = {},
  sourceByArticle = {},
  incomingTcType = "",
  fallbackStandards = [],
  effectiveTcId = "",
  sourceLoading = false,
  onSourceRowsChange,
}) => {
  const { executeGet } = useApi();
  const [enrichedRowsByArticle, setEnrichedRowsByArticle] = useState<Record<string, SourceRawMaterialRow[]>>({});
  const [enrichedStandardsByArticle, setEnrichedStandardsByArticle] = useState<Record<string, string[]>>({});
  const typeUpper = String(incomingTcType || "").toUpperCase();
  const isRmdf = typeUpper === "RMDF";
  const showWeights = typeUpper === "V4" || typeUpper === "ATU" || typeUpper === "GOTS";
  const showAdditives = typeUpper === "V4";
  const showPercentage = typeUpper !== "V3";
  // 86ajfgrcn: testers want full descriptive column names in this table.
  const articleHeader = typeUpper === "FPF" ? "Product Incoming Lot No" : typeUpper === "V3" ? "Product Incoming Product Number" : "Product Incoming Article Number";

  // Always load the source entity when step 2 opens. Search/list responses are
  // useful for rendering quickly, but get-incoming-tc-info is authoritative for
  // the selected source ID, article standards, and raw-material information.
  useEffect(() => {
    setEnrichedRowsByArticle({});
    setEnrichedStandardsByArticle({});
    if (!effectiveTcId || !typeUpper || allArticles.length === 0) return;

    let cancelled = false;
    (async () => {
      const updates: Record<string, SourceRawMaterialRow[]> = {};
      const standardsUpdates: Record<string, string[]> = {};
      await Promise.all(
        allArticles.map(async (article) => {
          try {
            const apiResponse = await executeGet(TCAppConfig.GET_INCOMING_TC_INFO, {
              incomingTcNoTcId: effectiveTcId,
              tcProductIncomingArticleNo: article,
              incomingTcType: typeUpper,
            });
            const products = unwrapIncomingProducts(apiResponse?.data?.response ?? apiResponse?.data);
            const matching = products.filter(
              (row: any) => String(row?.tcProductArticleNo || "") === String(article)
            );
            const productsForArticle = matching.length > 0 ? matching : products.slice(0, 1);
            const articleStandards = Array.from(
              new Set(
                productsForArticle
                  .flatMap((product: any) => (Array.isArray(product?.standards) ? product.standards : []))
                  .map((standard: any) => String(standard || "").trim())
                  .filter(Boolean)
              )
            );
            const rows = productsForArticle.flatMap((product: any) => {
              const rawMaterials = Array.isArray(product?.tcProductRawMaterialInformation)
                ? product.tcProductRawMaterialInformation
                : [];
              return rawMaterials.map((rm: any) => ({
                tcProductCategoryCode: product?.tcProductCategoryCode || "",
                tcProductDetailCode: product?.tcProductDetailCode || "",
                rawMaterialCode: rm?.tcProductRawMaterialCode || rm?.rawMaterialCode || "",
                rawMaterialPercentage:
                  Number(rm?.tcProductRawMaterialPercentage ?? rm?.rawMaterialPercentage) || 100,
                standard: articleStandards.length > 0 ? articleStandards.join(", ") : "",
                isCertified: rm?.isCertified ?? rm?.is_certified ?? "",
                tcProductNetShippingWeight: Number(product?.tcProductNetShippingWeight) || 0,
                tcProductSupplementaryWeight: Number(product?.tcProductSupplementaryWeight) || 0,
                tcProductAdditivesWeight: Number(product?.tcProductAdditivesWeight) || 0,
              }));
            });
            if (rows.length > 0) {
              updates[article] = rows;
            }
            if (articleStandards.length > 0) {
              standardsUpdates[article] = articleStandards;
            }
          } catch (error) {
            console.error(`Failed to enrich incoming source rows for ${article}:`, error);
          }
        })
      );
      if (!cancelled && Object.keys(updates).length > 0) {
        setEnrichedRowsByArticle((prev) => ({ ...prev, ...updates }));
        onSourceRowsChange?.(updates);
      }
      if (!cancelled && Object.keys(standardsUpdates).length > 0) {
        setEnrichedStandardsByArticle((prev) => ({ ...prev, ...standardsUpdates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveTcId, typeUpper, allArticles.join("|"), onSourceRowsChange]);

  const rows = useMemo(() => {
    return allArticles.map((article) => {
      const mappedRows =
        Array.isArray(rawMaterialsByArticle?.[article]) && rawMaterialsByArticle[article].length > 0
          ? rawMaterialsByArticle[article]
          : sourceByArticle?.[article]
          ? [sourceByArticle[article]]
          : [];
      const rawRows = enrichedRowsByArticle[article]?.length
        ? enrichedRowsByArticle[article]
        : mappedRows;
      const primary = rawRows[0] || {};
      const standards = isRmdf
        ? ["RMDF"]
        : enrichedStandardsByArticle[article]?.length
        ? enrichedStandardsByArticle[article]
        : Array.isArray(standardsByArticle?.[article]) && standardsByArticle[article].length > 0
        ? standardsByArticle[article]
        : (() => {
            const fromRows = Array.from(
              new Set(rawRows.map((row) => String(row?.standard || "").trim()).filter(Boolean))
            );
            return fromRows.length > 0 ? fromRows : fallbackStandards;
          })();
      const categoryCodes = Array.from(
        new Set(rawRows.map((row) => String(row?.tcProductCategoryCode || "").trim()).filter(Boolean))
      );
      const detailCodes = Array.from(
        new Set(rawRows.map((row) => String(row?.tcProductDetailCode || "").trim()).filter(Boolean))
      );
      return {
        article,
        standards,
        categoryCodes,
        detailCodes,
        rawRows,
        netWeight: primary?.tcProductNetShippingWeight,
        supplementaryWeight: primary?.tcProductSupplementaryWeight,
        additivesWeight: primary?.tcProductAdditivesWeight,
      };
    });
  }, [allArticles, rawMaterialsByArticle, sourceByArticle, standardsByArticle, isRmdf, enrichedRowsByArticle, enrichedStandardsByArticle, fallbackStandards]);

  const selectedSet = useMemo(() => new Set(selectedArticles), [selectedArticles]);
  const allSelected = allArticles.length > 0 && allArticles.every((article) => selectedSet.has(article));
  const someSelected = allArticles.some((article) => selectedSet.has(article));

  const syncStandardsMap = (articles: string[]) => {
    const nextMap = articles.reduce<Record<string, string[]>>((acc, article) => {
      const rowStandards = rows.find((row) => row.article === article)?.standards || [];
      const sourceStandards = isRmdf
        ? ["RMDF"]
        : enrichedStandardsByArticle[article]?.length
        ? enrichedStandardsByArticle[article]
        : Array.isArray(standardsByArticle?.[article]) && standardsByArticle[article].length > 0
        ? standardsByArticle[article]
        : rowStandards.length > 0
        ? rowStandards
        : componentData?.[article]?.length
        ? componentData[article]
        : fallbackStandards;
      acc[article] = sourceStandards;
      return acc;
    }, {});
    setComponentData(nextMap);
  };

  // The backend requires the stored standards to EXACTLY match the incoming
  // article's standards. When source enrichment resolves after the user has
  // already selected rows, re-sync the standards map with the full list.
  useEffect(() => {
    if (selectedArticles.length === 0) return;
    const needsUpdate = selectedArticles.some((article) => {
      const enriched = enrichedStandardsByArticle[article] || [];
      if (enriched.length === 0) return false;
      const current = componentData?.[article] || [];
      return JSON.stringify([...current].sort()) !== JSON.stringify([...enriched].sort());
    });
    if (needsUpdate) {
      syncStandardsMap(selectedArticles);
    }
  }, [enrichedStandardsByArticle]);

  const handleToggleAll = () => {
    const nextArticles = allSelected ? [] : [...allArticles];
    syncStandardsMap(nextArticles);
    onSelectionChange(nextArticles);
  };

  const handleToggleArticle = (article: string) => {
    const nextArticles = selectedSet.has(article)
      ? selectedArticles.filter((item) => item !== article)
      : [...selectedArticles, article];
    syncStandardsMap(nextArticles);
    onSelectionChange(nextArticles);
  };

  if (allArticles.length === 0) {
    if (effectiveTcId && sourceLoading) {
      return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <LoaderComponent />
          <Typography variant="body2" color="textSecondary">
            Loading incoming product articles...
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          {effectiveTcId
            ? "No incoming product articles were found on the selected certificate."
            : "No incoming products available. Please search and select the certificate number in the previous step."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Select the incoming product article(s) to attach. Standards are taken from the incoming
        certificate{isRmdf ? ' (fixed to "RMDF" for reclaimed material declarations)' : ""}.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: showWeights ? 1480 : 900 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#2C4748",
                "& .MuiTableCell-head": {
                  color: "#fff",
                  fontWeight: "bold",
                  lineHeight: 1.25,
                  whiteSpace: "normal",
                },
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={handleToggleAll}
                  inputProps={{ "aria-label": "Select all incoming products" }}
                  sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" }, "&.MuiCheckbox-indeterminate": { color: "#fff" } }}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 190 }}>{articleHeader}</TableCell>
              <TableCell sx={{ minWidth: 190 }}>Product Incoming TC Standard</TableCell>
              <TableCell sx={{ minWidth: 230 }}>Product Incoming Product Category Code</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Product Incoming Product Detail Code</TableCell>
              <TableCell sx={{ minWidth: 280 }}>Product Incoming Raw Material and Composition</TableCell>
              {showWeights && <TableCell align="right" sx={{ minWidth: 190 }}>Product Incoming Net Weight</TableCell>}
              {showWeights && <TableCell align="right" sx={{ minWidth: 230 }}>Product Incoming Supplementary Weight</TableCell>}
              {showAdditives && <TableCell align="right" sx={{ minWidth: 190 }}>Product Incoming Additives Weight</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.article}
                hover
                selected={selectedSet.has(row.article)}
                onClick={() => handleToggleArticle(row.article)}
                sx={{ cursor: "pointer", verticalAlign: "top" }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedSet.has(row.article)}
                    onChange={() => handleToggleArticle(row.article)}
                    onClick={(event) => event.stopPropagation()}
                    inputProps={{ "aria-label": `Select ${row.article}` }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.article}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {row.standards.length > 0 ? (
                      row.standards.map((standard) => (
                        <Chip key={standard} label={standard} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.categoryCodes.join(", ") || "-"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.detailCodes.join(", ") || "-"}</Typography>
                </TableCell>
                <TableCell>
                  {row.rawRows.length > 0 ? (
                    row.rawRows.map((rm, index) => (
                      <Typography key={`${row.article}-rm-${index}`} variant="body2">
                        {rm.rawMaterialCode || "-"}
                        {showPercentage && formatPercentage(rm.rawMaterialPercentage)
                          ? ` (${formatPercentage(rm.rawMaterialPercentage)})`
                          : ""}
                        {String(rm.isCertified || "").toUpperCase() === "Y" ? " • Certified" : ""}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                {showWeights && (
                  <TableCell align="right">
                    <Typography variant="body2">{formatWeight(row.netWeight)}</Typography>
                  </TableCell>
                )}
                {showWeights && (
                  <TableCell align="right">
                    <Typography variant="body2">{formatWeight(row.supplementaryWeight)}</Typography>
                  </TableCell>
                )}
                {showAdditives && (
                  <TableCell align="right">
                    <Typography variant="body2">{formatWeight(row.additivesWeight)}</Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default IncomingTcStandard;
